import '@angular/localize';

/**
 * Standardized file names for definition template version control.
 */
export enum DefinitionFileNames {
  PageTemplate = 'pageTemplate',
  DataCardTemplate = 'dataCardTemplate'
}

/**
 * Interface representing a group of project models organized by their domain.
 */
export interface ProjectModelGroup {
  domainKey: string;
  domainName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  models: any[];
}

export const DATABASE_CONFIG = {
  texts: {
    pageTitle: $localize`:@@database.pageTitle:Database`,
    addFieldBtn: $localize`:@@database.addFieldBtn:Add Field`,
    editFieldTitle: $localize`:@@database.editFieldTitle:Edit Field`,
    addFieldTitle: $localize`:@@database.addFieldTitle:Add Field`,
    deleteFieldConfirm: $localize`:@@database.deleteFieldConfirm:WARNING: Deleting a field will permanently remove it from the model and delete all the data related to this field in the database. Are you sure you want to delete this field?`,
    invalidFieldName: $localize`:@@database.invalidFieldName:Field name must be camelCase or snake_case, start with a lowercase letter, and contain no spaces or special characters except underscores.`,
    duplicateFieldName: $localize`:@@database.duplicateFieldName:Field name must be unique within the model.`,
    fieldEnumLabel: $localize`:@@database.fieldEnumLabel:Enum Values`,
    fieldEnumHint: $localize`:@@database.fieldEnumHint:Type a value and press Enter. Duplicates will be automatically removed.`,
    fieldDefaultFnLabel: $localize`:@@database.fieldDefaultFnLabel:Default Function`,
    fieldDefaultFnHint: $localize`:@@database.fieldDefaultFnHint:Auto-generates value on the database level.`,
    fieldExplanationTitle: $localize`:@@database.fieldExplanationTitle:Field Properties Explanation`,
    fixNameBtn: $localize`:@@database.fixNameBtn:Fix Name`,
    addEnumBtn: $localize`:@@database.addEnumBtn:Add`,
    enumDuplicateError: $localize`:@@database.enumDuplicateError:This enum value already exists.`,
    enumEmptyError: $localize`:@@database.enumEmptyError:Enum value cannot be empty.`,
    migrationWarning: $localize`:@@database.migrationWarning:WARNING: We need to generate a new migration and new backend code to apply these changes, please make sure to run the migration and update the backend code after making these changes using the Rapider Dev Agent`,
    invalidDropPosition: $localize`:@@database.invalidDropPosition:You cannot drop a field over the ID field or below the auto-generated system properties.`,
    pageSubtitle: $localize`:@@database.pageSubtitle:Manage your project's data architecture, tables, and AI configurations.`,
    searchPlaceholder: $localize`:@@database.searchPlaceholder:Search models by name or description...`,
    emptyState: $localize`:@@database.emptyState:No database models found matching your criteria.`,
    openDetailsBtn: $localize`:@@database.openDetailsBtn:Open Full Details`,
    backToList: $localize`:@@database.backToList:Back to Database`,
    allDomains: $localize`:@@database.allDomains:All Domains`,
    collapseAll: $localize`:@@database.collapseAll:Collapse All`,
    expandAll: $localize`:@@database.expandAll:Expand All`,
    noDescription: $localize`:@@database.noDescription:No description provided.`,
    defaultDomain: $localize`:@@database.defaultDomain:Default`,
    mainRecordDetails: $localize`:@@database.mainRecordDetails:Main Record Details`,

    // View Modes
    viewData: $localize`:@@database.viewData:Data`,
    viewSpreadsheet: $localize`:@@database.viewSpreadsheet:Spreadsheet`,
    viewSchema: $localize`:@@database.viewSchema:Schema`,

    // View Mode Tooltips
    tooltipViewData: $localize`:@@database.tooltipViewData:View and manage individual records of this model.`,
    tooltipViewSpreadsheet: $localize`:@@database.tooltipViewSpreadsheet:Edit your data in a spreadsheet format.`,
    tooltipViewSchema: $localize`:@@database.tooltipViewSchema:View and manage the schema, AI agent, and database of this model.`,

    // AI Context Descriptions Modal
    aiContextBtnTooltip: $localize`:@@database.aiContextBtnTooltip:Display AI Context Descriptions`,
    aiContextModalTitle: $localize`:@@database.aiContextModalTitle:Data Models AI Context`,
    aiContextModalClose: $localize`:@@database.aiContextModalClose:Close`,
    aiContextModalCopy: $localize`:@@database.aiContextModalCopy:Copy to Clipboard`,
    aiContextModalCopied: $localize`:@@database.aiContextModalCopied:Copied!`,
    aiContextLoading: $localize`:@@database.aiContextLoading:Generating AI context descriptions...`,

    // Detail page texts
    detailHeaderTitle: $localize`:@@database.detailHeaderTitle:Model Details`,
    fieldsTitle: $localize`:@@database.fieldsTitle:Fields`,
    relationsTitle: $localize`:@@database.relationsTitle:Relations`,
    endpointsTitle: $localize`:@@database.endpointsTitle:API Endpoints`,
    previewTitleTemplate: $localize`:@@database.previewTitleTemplate:Display Title Template`,
    previewLocalDiagramTitle: $localize`:@@database.previewLocalDiagramTitle:Model Relations Diagram`,

    // Table Headers (Fields & Relations)
    tableFieldName: $localize`:@@database.tableFieldName:Name`,
    tableFieldType: $localize`:@@database.tableFieldType:Type`,
    tableFieldFlags: $localize`:@@database.tableFieldFlags:Flags`,
    tableFieldDesc: $localize`:@@database.tableFieldDesc:Description`,
    tableRelName: $localize`:@@database.tableRelName:Name`,
    tableRelType: $localize`:@@database.tableRelType:Type`,
    tableRelTarget: $localize`:@@database.tableRelTarget:Target Model`,
    tableRelKeys: $localize`:@@database.tableRelKeys:Keys (From → To)`,
    tableRelDesc: $localize`:@@database.tableRelDesc:Description`,

    // Table Headers (Endpoints)
    epMethod: $localize`:@@database.epMethod:HTTP Method`,
    epName: $localize`:@@database.epName:Name`,
    epPath: $localize`:@@database.epPath:Path`,
    epDesc: $localize`:@@database.epDesc:Description`,
    epMocked: $localize`:@@database.epMocked:Mocked`,

    // AI Config
    aiConfigTitle: $localize`:@@database.aiConfigTitle:AI Agent Configuration`,
    previewAiVectorSearch: $localize`:@@preview.aiVectorSearch:Vector Search Fields`,
    previewAiPlanning: $localize`:@@preview.aiPlanning:Planning Operations`,
    previewAiCreate: $localize`:@@preview.aiCreate:Entity Item Create`,
    previewAiUpdate: $localize`:@@preview.aiUpdate:Entity Item Update`,
    previewAiRead: $localize`:@@preview.aiRead:Entity Read`,
    previewAiDelete: $localize`:@@preview.aiDelete:Entity Item Delete`,
    previewAiRequiredFields: $localize`:@@preview.aiRequiredFields:Required Fields`,
    previewAiImmutableFields: $localize`:@@preview.aiImmutableFields:Immutable Fields`,
    previewAiRequiredRelations: $localize`:@@preview.aiRequiredRelations:Required Relations`,
    previewAiAutoGenerate: $localize`:@@preview.aiAutoGenerate:Auto Generate Fields`,
    previewAiRequireUserConf: $localize`:@@preview.aiRequireUserConf:Require User Confirmation`,
    previewAiCustomValidation: $localize`:@@preview.aiCustomValidation:Custom Validation Prompt`,
    previewAiRestrictedStates: $localize`:@@preview.aiRestrictedStates:Restricted States`,
    previewAiPrompt: $localize`:@@preview.aiPrompt:Additional Prompt`,
    previewAiContextLimit: $localize`:@@preview.aiContextLimit:Default Context Limit`,
    previewAiPageSize: $localize`:@@preview.aiPageSize:Default Page Size`,
    previewAiHiddenFields: $localize`:@@preview.aiHiddenFields:Hidden Fields`,
    previewAiMaskedFields: $localize`:@@preview.aiMaskedFields:Masked Fields`,
    previewAiCaptchaConf: $localize`:@@preview.aiCaptchaConf:Require Captcha Confirmation`,
    previewAiCascading: $localize`:@@preview.aiCascading:Cascading Relations`,
  },
  notifications: {
    fetchFailed: $localize`:@@database.fetchFailed:Failed to load database models.`,
    fetchDetailFailed: $localize`:@@database.fetchDetailFailed:Failed to load model details.`
  },
  dataCard: {
    loading: $localize`:@@database.dataCard.loading:Loading data card...`,
    generating: $localize`:@@database.dataCard.generating:AI is designing the layout for this record...`,
    noData: $localize`:@@database.dataCard.noData:No data available to display.`,
    rawView: $localize`:@@database.dataCard.rawView:Raw Data View`,
    templateView: $localize`:@@database.dataCard.templateView:Template View`,
    improveWithAi: $localize`:@@database.dataCard.improveWithAi:Improve with AI`,
    improveModalTitle: $localize`:@@database.dataCard.improveModalTitle:Improve Template with AI`,
    improveModalDesc: $localize`:@@database.dataCard.improveModalDesc:Describe what you want to change or improve in the current data card template.`,
    promptPlaceholder: $localize`:@@database.dataCard.promptPlaceholder:e.g., Change the background color to light blue, or show the related items as a vertical list instead of chips...`,
    sendButton: $localize`:@@database.dataCard.sendButton:Improve Template`,
    uploadButton: $localize`:@@database.dataCard.uploadButton:Upload Reference Files`,
    cancelButton: $localize`:@@database.dataCard.cancelButton:Cancel`,
    improving: $localize`:@@database.dataCard.improving:Improving template...`
  }
};

export const DATA_INSIGHT_TIMELINE_CONFIG = {
  texts: {
    title: $localize`:@@dataInsight.title:Data Insight`,
    description: $localize`:@@dataInsight.description:This timeline shows the history of data operations executed via the AI Chat, you can click on each event to see the details of the affected records and their relations.`,
    viewDetails: $localize`:@@dataInsight.viewDetails:View Details & Relations`,
    relatedData: $localize`:@@dataInsight.relatedData:Related Data:`,
    mainRecordDetails: $localize`:@@dataInsight.mainRecordDetails:Main Record Details`,
    noData: $localize`:@@dataInsight.noData:No details available.`,
    fetching: $localize`:@@dataInsight.fetching:Fetching details...`,
    noEvents: $localize`:@@dataInsight.noEvents:No data operations recorded yet.`,
    recordsDeleted: $localize`:@@dataInsight.recordsDeleted:Record(s) Deleted:`
  }
};


export const DATA_INSIGHT_CONFIG = {
  texts: {
    title: $localize`:@@dataInsight.title:Data Insight`,
    description: $localize`:@@dataInsight.description:This timeline shows the history of data operations executed via the AI Chat, you can click on each event to see the details of the affected records and their relations.`,
    viewDetails: $localize`:@@dataInsight.viewDetails:View Details & Relations`,
    relatedData: $localize`:@@dataInsight.relatedData:Related Data:`,
    mainRecordDetails: $localize`:@@dataInsight.mainRecordDetails:Main Record Details`,
    noData: $localize`:@@dataInsight.noData:No details available.`,
    fetching: $localize`:@@dataInsight.fetching:Fetching details...`,
    noEvents: $localize`:@@dataInsight.noEvents:No data operations recorded yet.`,
    recordsDeleted: $localize`:@@dataInsight.recordsDeleted:Record(s) Deleted:`
  }
};

/**
 * PURE FUNCTION: Extracts the `uiEntityConfig` from a project model's metadata.
 * @param {any} model - The full project model payload from the NgRx store.
 * @returns {any | null} The parsed uiEntityConfig object if found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractUiEntityConfig(model: any): any | null {
  if (!model?.metadata || !Array.isArray(model.metadata)) return null;
  const configMeta = model.metadata.find((m: any) => m.key === 'uiEntityConfig');
  if (!configMeta?.value) return null;
  try {
    return typeof configMeta.value === 'string' ? JSON.parse(configMeta.value) : configMeta.value;
  } catch (e) {
    return configMeta.value;
  }
}

/**
 * PURE FUNCTION: Builds the `include` array for LoopBack filters based on relations config.
 * Instructs the backend API to eagerly return related data payloads alongside the main query.
 * @param {any[]} relationsConfig - Array of relation configuration objects.
 * @returns {any[]} Array of LoopBack include definitions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildIncludeFilter(relationsConfig: any[]): any[] {
  if (!relationsConfig || !Array.isArray(relationsConfig)) return [];
  return relationsConfig.map(rel => ({ relation: rel.name }));
}

/**
 * PURE FUNCTION: Builds the exact API path with `where` and `include` filters.
 * @param {string} modelName - The base model name.
 * @param {string[]} recordIds - The IDs of the targeted records to fetch.
 * @param {any[]} relationsConfig - Optional array of relations to dynamically include.
 * @returns {string} The formatted API path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildFetchRecordsWithRelationsPath(modelName: string, recordIds: string[], relationsConfig: any[] = []): string {
  if (!modelName || !recordIds || recordIds.length === 0) return '';
  const base = toKebabCase(modelName);
  const pluralBase = base.endsWith('s') ? base : `${base}s`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filter: any = { where: { id: { inq: recordIds } } };

  const includes = buildIncludeFilter(relationsConfig);
  if (includes.length > 0) {
    filter.include = includes;
  }

  return `${pluralBase}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
}

/**
 * PURE FUNCTION: (Obsolete) Previously injected a 'Select' action into a list grid config to allow master-detail viewing.
 * Now bypassed as we use row clicks and hovers natively.
 * @param {any} baseConfig - The original list grid config extracted from uiEntityConfig.
 * @param {string} selectText - The localized text for the select action button.
 * @returns {any} The unmodified base configuration object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function enrichGridConfigWithSelectAction(baseConfig: any, selectText: string): any {
  return baseConfig || { columns: [], itemActions: [] };
}

/**
 * PURE FUNCTION: Determines if a relation should be displayed as a list grid or JSON viewer.
 * @param {string} relationType - The LoopBack relation type (e.g., 'referencesMany', 'belongsTo').
 * @param {any} data - The actual data payload to serve as a fallback evaluation constraint.
 * @returns {'grid' | 'json'} The chosen display component semantic.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRelationDisplayType(relationType: string, data: any): 'grid' | 'json' {
  if (['referencesMany', 'hasMany'].includes(relationType)) return 'grid';
  if (['belongsTo', 'hasOne'].includes(relationType)) return 'json';
  return Array.isArray(data) ? 'grid' : 'json'; // Default generic fallback
}

/**
 * PURE FUNCTION: Generates a lightweight, fallback List-Grid configuration for visualizing generic arrays of fetched entities.
 * Dynamically resolves columns by analyzing the properties of the first element.
 * @param {any[]} data - The data array targeted for rendering.
 * @returns {any} A resolved grid configuration object mapping standard Rapider-list-grid expectations.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generateTimelineGridConfig(data: any[]): any {
  if (!data || data.length === 0) return { columns: [] };
  const firstItem = Array.isArray(data) ? data[0] : data;
  if (!firstItem || typeof firstItem !== 'object') return { columns: [] };

  const columns = Object.keys(firstItem)
    .filter(key => !key.startsWith('__rapider')) // Exclude deep internal metadata keys
    .map(key => ({
      fieldName: key,
      // Format camelCase to Title Case with spaces
      title: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
      type: typeof firstItem[key] === 'number' ? 'number' : 'text',
      visible: true
    }));

  return { columns, searchable: true, filterable: false, sortable: true };
}

/**
 * PURE FUNCTION: Retrieves a list grid config from a target model cached in the NgRx Store, with a dynamic fallback.
 * @param {string} modelName - The target related model name.
 * @param {any[]} cachedModels - All project models currently in the NgRx state cache.
 * @param {any[]} fallbackData - The data array itself, to dynamically generate a schema if the target model is missing.
 * @returns {any} A resolved grid configuration object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGridConfigForModel(modelName: string, cachedModels: any[], fallbackData: any[]): any {
  if (!modelName || !cachedModels) return generateTimelineGridConfig(fallbackData);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const model = cachedModels.find((m: any) =>
    (m.name && m.name.toLowerCase() === modelName.toLowerCase()) ||
    (m.dataTableName && m.dataTableName.toLowerCase() === modelName.toLowerCase())
  );
  const uiConfig = extractUiEntityConfig(model);
  return uiConfig?.config?.listGridConfig || generateTimelineGridConfig(fallbackData);
}

/**
 * PURE FUNCTION: Builds a JsonViewerClassMap based on the payload data of an UPDATE operation.
 * Highlights the fields that were modified by painting their rows orange.
 * @param {any} payloadData - The payload sent during the update operation.
 * @returns {any} The class map object ready for the rapider-json-viewer.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildUpdateDiffClassMap(payloadData: any): any {
  if (!payloadData || typeof payloadData !== 'object') return {};

  const classMap: any = {};

  // Separate rapider metadata out if present before finding keys, and strictly ignore the 'id' field
  const cleanKeys = Object.keys(payloadData).filter(k => !k.startsWith('__rapider') && k !== 'id');

  cleanKeys.forEach(key => {
    // Highlight the specific changed key row
    classMap[key] = {
      line: 'bg-warning/10 rounded px-1',
      key: 'text-warning font-semibold',
      value: 'text-warning font-semibold',
    };
    // Ensure any nested objects under the changed key are also highlighted
    classMap[`${key}.*`] = {
      key: 'text-warning',
      value: 'text-warning',
    };
  });

  return classMap;
}

/**
 * PURE FUNCTION: Retrieves the title for a relation's target model from the cached NgRx store.
 * @param {string} targetModelName - The name of the target model to look up.
 * @param {any[]} cachedModels - The list of cached models from the store.
 * @returns {string} The resolved title, or the raw name if not found.
 */
export function getTargetModelTitle(targetModelName: string, cachedModels: any[]): string {
  if (!targetModelName || !cachedModels) return targetModelName || 'Unknown';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const model = cachedModels.find((m: any) =>
    (m.name && m.name.toLowerCase() === targetModelName.toLowerCase()) ||
    (m.dataTableName && m.dataTableName.toLowerCase() === targetModelName.toLowerCase())
  );
  return model?.title || model?.name || targetModelName;
}

/**
 * PURE FUNCTION: Builds the exact API path and URL query string to fetch multiple records by ID securely.
 * Automatically resolves the pluralization and formats the Loopback 'inq' filter.
 * @param {string} modelName - The base model name (e.g., 'Issue' or 'UserProfile')
 * @param {string[]} recordIds - An array of record IDs to fetch.
 * @returns {string} The fully formed API path string with encoded query params.
 */
export function buildFetchRecordsPath(modelName: string, recordIds: string[]): string {
  if (!modelName || !recordIds || recordIds.length === 0) return '';
  const base = toKebabCase(modelName);
  const pluralBase = base.endsWith('s') ? base : `${base}s`;
  const filter = { where: { id: { inq: recordIds } } };
  return `${pluralBase}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
}

/**
 * PURE FUNCTION: Generates semantic tailwind classes and icon definitions based on operation type.
 * Ensures the timeline looks visually distinct based on the data mutation.
 * @param {string} type - The explicit operation type ('CREATE', 'READ', 'UPDATE', 'DELETE').
 * @returns {{ icon: string, colorClass: string, bgClass: string }} UI formatting configurations.
 */
export function getOperationTheme(type: string): { icon: string, colorClass: string, bgClass: string } {
  switch (type?.toUpperCase()) {
    case 'CREATE':
      return { icon: 'fa-plus-circle', colorClass: 'text-success', bgClass: 'bg-success/10 border-success/20' };
    case 'READ':
      return { icon: 'fa-eye', colorClass: 'text-info', bgClass: 'bg-info/10 border-info/20' };
    case 'UPDATE':
      return { icon: 'fa-pen', colorClass: 'text-warning', bgClass: 'bg-warning/10 border-warning/20' };
    case 'DELETE':
      return { icon: 'fa-trash', colorClass: 'text-danger', bgClass: 'bg-danger/10 border-danger/20' };
    default:
      return { icon: 'fa-database', colorClass: 'text-secondary-text', bgClass: 'bg-alt-background border-secondary-border' };
  }
}

/**
 * Pure function to safely extract the AI Agent Configuration out of the project model metadata array.
 * @param {any} model - The full project model payload.
 * @returns {any | null} The AI Agent Configuration object if it exists.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractAiConfigFromMetadata(model: any): any | null {
  if (!model) return null;

  if (Array.isArray(model.metadata)) {
    const configObj = model.metadata.find((m: any) => m.key === 'aiAgentConfig' || m.key === 'aiConfig');
    if (configObj && configObj.value) return configObj.value;
  }

  if (model.metadata?.aiAgentConfig) return model.metadata.aiAgentConfig;
  if (model.aiAgentConfig) return model.aiAgentConfig;

  return null;
}

/**
 * Pure function to safely extract the Title Field Template from the project model metadata array.
 * @param {any} model - The full project model payload.
 * @returns {string | null} The template string if it exists.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractTitleTemplateFromMetadata(model: any): string | null {
  if (!model) return null;

  if (Array.isArray(model.metadata)) {
    const templateObj = model.metadata.find((m: any) => m.key === 'titleFieldTemplate');
    if (templateObj && templateObj.value) return templateObj.value;
  }

  if (model.titleFieldTemplate) return model.titleFieldTemplate;
  return null;
}

/**
 * Pure function to safely check if a parsed AI Agent Config object actually contains configuration keys.
 * @param {any} config - The extracted AI Agent Configuration object.
 * @returns {boolean} True if valid configuration exists.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasDetailedAiConfig(config: any): boolean {
  return config && typeof config === 'object' && Object.keys(config).length > 0;
}

/**
 * Pure function to format a dataTableName (camelCase or PascalCase) into a readable Title Case string.
 * @param {string} name - The raw table name (e.g., 'userProfile' or 'InvoiceItem').
 * @returns {string} The formatted title (e.g., 'User Profile').
 */
export function formatDataTableNameAsTitle(name: string): string {
  if (!name) return '';
  const spaced = name.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Pure function to convert a CamelCase or PascalCase string into kebab-case.
 * @param {string} str - The input string.
 * @returns {string} The kebab-cased string.
 */
export function toKebabCase(str: string): string {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

/**
 * Pure function to build a full route path combining the base model name with the endpoint's specific path.
 * @param {string} modelName - The base model name.
 * @param {string} endpointPath - The endpoint specific path.
 * @returns {string} The formatted full path.
 */
export function buildFullPath(modelName: string, endpointPath: string): string {
  const base = toKebabCase(modelName);
  const pluralBase = base.endsWith('s') ? base : `${base}s`;

  if (!endpointPath || endpointPath.trim() === '') {
    return pluralBase;
  }
  return `${pluralBase}/${endpointPath}`;
}

/**
 * Pure function to determine semantic Tailwind classes based on the HTTP method.
 * @param {string} method - The HTTP method string.
 * @returns {string} Space-separated Tailwind classes.
 */
export function getMethodColorClass(method: string): string {
  const normalized = (method || '').toLowerCase();
  switch (normalized) {
    case 'get':
      return 'text-info bg-info/10 border-info/20';
    case 'post':
      return 'text-success bg-success/10 border-success/20';
    case 'put':
    case 'patch':
      return 'text-warning bg-warning/10 border-warning/20';
    case 'del':
    case 'delete':
      return 'text-danger bg-danger/10 border-danger/20';
    default:
      return 'text-secondary-text bg-alt-background border-secondary-border';
  }
}

/**
 * Pure function to toggle the boolean value of a specific key within a state record.
 * @param {Record<string, boolean>} currentState - The current dictionary state.
 * @param {string} key - The key to toggle.
 * @returns {Record<string, boolean>} A newly constructed record.
 */
export function toggleRecordState(currentState: Record<string, boolean>, key: string): Record<string, boolean> {
  return { ...currentState, [key]: !currentState[key] };
}

/**
 * Pure function to set all boolean values of a specific state record to a target value.
 * @param {Record<string, boolean>} currentState - The current dictionary state.
 * @param {boolean} targetState - The boolean state to enforce.
 * @returns {Record<string, boolean>} A newly constructed record.
 */
export function setAllRecordStates(currentState: Record<string, boolean>, targetState: boolean): Record<string, boolean> {
  const updatedState: Record<string, boolean> = {};
  Object.keys(currentState).forEach(key => {
    updatedState[key] = targetState;
  });
  return updatedState;
}

/**
 * PURE FUNCTION: Groups an array of database models by their respective domains and sorts them alphabetically.
 * CRITICAL FIX: Creates a shallow clone of each model before applying default properties to prevent 
 * "object is not extensible" errors caused by mutating deeply frozen NgRx/Signal state objects.
 * @param {any[]} rawModels - The raw array of model objects retrieved from the API or State.
 * @returns {ProjectModelGroup[]} An array of grouped models formatted for the UI accordion.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function groupAndSortModels(rawModels: any[]): ProjectModelGroup[] {
  if (!rawModels || !Array.isArray(rawModels)) {
    return [];
  }

  const groups: Record<string, ProjectModelGroup> = {};

  rawModels.forEach(rawModel => {
    // 1. CLONE THE OBJECT: Avoid mutating the frozen reference from NgRx store
    const model = { ...rawModel };

    // 2. Safely apply dynamic/fallback properties to the cloned instance
    if (!model.title) {
      model.title = model.dataTableName || model.name || 'Untitled Model';
    }

    const domainKey = model.domainKey || 'default';
    const domainName = model.domainName || 'Default Domain';

    // 3. Group the cloned object safely
    if (!groups[domainKey]) {
      groups[domainKey] = {
        domainKey: domainKey,
        domainName: domainName,
        models: []
      };
    }

    groups[domainKey].models.push(model);
  });

  // 4. Convert the dictionary back to an array and sort it alphabetically by Domain Name
  return Object.values(groups).sort((a, b) => {
    const nameA = a.domainName || '';
    const nameB = b.domainName || '';
    return nameA.localeCompare(nameB);
  });
}

/**
 * PURE FUNCTION: Extracts the displayed model IDs based on active search and domain filters.
 * If no filters are actively applied, it returns an empty array to instruct the backend to fetch all models natively.
 * @param {string} searchQuery - The active search string.
 * @param {string | null} selectedDomain - The active domain filter.
 * @param {ProjectModelGroup[]} filteredGroups - The currently visible, filtered groups.
 * @returns {string[]} An array of project model IDs.
 */
export function extractDisplayedModelIds(searchQuery: string, selectedDomain: string | null, filteredGroups: ProjectModelGroup[]): string[] {
  if (!searchQuery && !selectedDomain) {
    return []; // Empty array triggers the backend to retrieve all models
  }

  const ids: string[] = [];
  for (const group of filteredGroups) {
    for (const model of group.models) {
      if (model.id) {
        ids.push(model.id);
      }
    }
  }

  return ids;
}

/**
 * Pure function to build the Loopback payload fetching tables for a given project ID.
 */
export function buildProjectModelsFilter(projectId: string): string {
  return JSON.stringify({
    where: { projectId },
    fields: {
      id: true,
      title: true,
      dataTableName: true,
      description: true,
      domainName: true,
      domainKey: true
    }
  });
}

/**
 * PURE FUNCTION: Generates a comprehensive, readable string detailing a project model,
 * its fields, relations, and the specific fields of its related models.
 * This is highly optimized for providing context to LLMs (Large Language Models)
 * to ensure they have the exact schema needed to write accurate data bindings.
 * * @param {any} modelDetails - The full details of the main project model containing fields and relations.
 * @param {any[]} allModels - The array of all cached models to look up related model details.
 * @returns {string} A formatted string detailing the entity and its related context.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildProjectModelContextString(modelDetails: any, allModels: any[]): string {
  if (!modelDetails) return 'No model details provided.';

  const name = modelDetails.name || modelDetails.dataTableName || 'Unknown Entity';
  const description = modelDetails.description || 'No description provided.';

  // Main model fields (all fields)
  const fields = modelDetails.fields && Array.isArray(modelDetails.fields)
    ? modelDetails.fields.map((f: any) => `- ${f.name} (${f.type})`).join('\n')
    : 'Unknown fields';

  // Main model relations
  const relations = modelDetails.relations && Array.isArray(modelDetails.relations)
    ? modelDetails.relations.map((r: any) => `- ${r.name} (${r.type} -> ${r.targetModel?.title || r.targetModel?.dataTableName || r.targetModel?.name || r.targetModel})`).join('\n')
    : 'No relations';

  let contextStr = `Entity: ${name}\nEntity Description: ${description}\n\nAvailable Fields:\n${fields}\n\nAvailable Relations:\n${relations}\n`;

  // Related Models Context
  if (modelDetails.relations && Array.isArray(modelDetails.relations) && modelDetails.relations.length > 0) {
    contextStr += `\n--- Related Entities Context ---\n`;

    const processedTargets = new Set<string>();

    modelDetails.relations.forEach((rel: any) => {
      // Safely extract the ID
      const targetId = typeof rel.targetModel === 'string' ? rel.targetModel : rel.targetModel?.id;

      if (targetId && !processedTargets.has(targetId)) {
        processedTargets.add(targetId);

        // Find the target model strictly by ID
        const targetModel = allModels.find((m: any) => m.id === targetId);

        if (targetModel) {
          const targetDesc = targetModel.description || 'No description provided.';
          const displayTitle = targetModel.title || targetModel.dataTableName || targetModel.name || targetId;

          // Filter out mixin properties for related models (e.g. audit fields like createdAt)
          let targetFieldsStr = 'Unknown fields';
          if (targetModel.fields && Array.isArray(targetModel.fields)) {
            targetFieldsStr = targetModel.fields
              .filter((f: any) => !f.isMixinProperty && !f.isHidden && !f.isId) // Exclude audit/mixin fields AND exclude hidden fields (isHidden) and exclude id fields (isId)
              .map((f: any) => `- ${f.name} (${f.type})`)
              .join('\n');
          }

          contextStr += `\nRelated Entity: ${displayTitle}\nDescription: ${targetDesc}\nFields (excluding audit fields):\n${targetFieldsStr}\n`;
        }
      }
    });
  }

  return contextStr;
}

/**
 * PURE FUNCTION: Builds a structured prompt for the AI agent to generate a Handlebars template for a data card.
 * @param {string} modelName - The name of the project model.
 * @param {any} modelDetails - The full model details containing description, fields, and relations.
 * @param {any[]} allModels - The array of all project models for relation context.
 * @param {any} [sampleData] - Optional sample data payload to provide the AI with explicit structure context.
 * @returns {string} The prompt string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildDataCardPrompt(modelName: string, modelDetails: any, allModels: any[], sampleData?: any): string {
  const modelContext = buildProjectModelContextString(modelDetails, allModels);

  return `
    You are an expert UI engineer. Generate a Handlebars template to display a single record of the database entity "${modelName}".
    
    ${modelContext}
    
    Sample Data for Reference: ${sampleData ? JSON.stringify(sampleData, null, 2) : 'No sample data provided'}
  `;
}

/**
 * PURE FUNCTION: Builds a structured prompt for the AI agent to improve an existing Handlebars template.
 * Injects the original data context alongside the current template and the user's specific improvement request.
 * @param {string} modelName - The name of the project model.
 * @param {any} modelDetails - The full model details containing description, fields, and relations.
 * @param {any[]} allModels - The array of all project models for relation context.
 * @param {string} currentTemplate - The HTML/Handlebars template currently in use.
 * @param {string} userPrompt - The user's requested changes.
 * @returns {string} The prompt string for the AI to process.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildImproveDataCardPrompt(modelName: string, modelDetails: any, allModels: any[], currentTemplate: string, userPrompt: string): string {
  const modelContext = buildProjectModelContextString(modelDetails, allModels);

  return `
   You previously generated a Handlebars template for the database entity "${modelName}".
    The user wants to improve or modify the template based on their feedback below.
    
    ${modelContext}

    Current Handlebars Template:
    \`\`\`html
    ${currentTemplate}
    \`\`\`

    <User Feedback / Improvement Request>
    "${userPrompt}"
    </User Feedback>

    Guidelines:
    - Incorporate the user's feedback to update the provided template.
  `;
}

/**
 * PURE FUNCTION: Safely extracts the HTML template string from the AI backend response.
 * Specifically checks for and parses the {"template": "..."} object format.
 * @param {any} response - The raw response from the AiFunctionCallController.
 * @returns {string | null} The extracted template or null.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractTemplateFromAiResponse(response: any): string | null {
  if (!response || !response.body) return null;

  const body = response.body;

  // 1. Check if the body itself has the template property mapped directly
  if (body.template && typeof body.template === 'string') {
    return cleanTemplateString(body.template);
  }

  // 2. Extract the content block from standard Loopback wrappers
  let content = body.data?.text || body.data?.content || body.message?.content || body.result || body.text;

  if (!content) return null;

  // 3. Attempt to parse the content as JSON if it's stringified
  if (typeof content === 'string') {
    content = content.trim();

    // Strip potential markdown JSON codeblocks before parsing
    content = content.replace(/^```json\n?|```\n?$/gm, '').trim();

    if (content.startsWith('{')) {
      try {
        const parsed = JSON.parse(content);
        if (parsed.template && typeof parsed.template === 'string') {
          return cleanTemplateString(parsed.template);
        }
      } catch (e) {
        // If it fails to parse, it might not actually be JSON. Fall through.
        console.warn('[Database Config] Failed to parse AI JSON response:', e);
      }
    }

    // Fallback: Treat as raw HTML string if JSON parsing didn't catch it
    return cleanTemplateString(content);
  }

  // 4. Handle if the inner data object natively contains the template
  if (typeof content === 'object' && content.template) {
    return cleanTemplateString(content.template);
  }

  return null;
}

/**
 * PURE FUNCTION: Helper to strip markdown block ticks generated by AI models.
 * @param {string} str - The raw template string.
 * @returns {string} The cleaned string.
 */
function cleanTemplateString(str: string): string {
  if (!str) return '';
  return str.replace(/^```html\n?|```handlebars\n?|```\n?$/gm, '').trim();
}

/**
 * PURE FUNCTION: Safely extracts the template string from a Definition database record's value.
 * Handles cases where the value is stored as a raw string, a JSON object, or a stringified JSON object.
 * @param {any} definitionValue - The value stored in the Definition record.
 * @returns {string | null} The extracted template string, or null if not found.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractTemplateFromDefinitionValue(definitionValue: any): string | null {
  if (!definitionValue) return null;

  if (typeof definitionValue === 'string') {
    try {
      const parsed = JSON.parse(definitionValue);
      if (parsed && typeof parsed === 'object' && parsed.template) {
        return parsed.template;
      }
    } catch (e) {
      // If it fails to parse, it might be a raw HTML string from legacy saves
      return definitionValue;
    }
  } else if (typeof definitionValue === 'object' && definitionValue.template) {
    return definitionValue.template;
  }

  return null;
}

/**
 * Pure function to explicitly define the payload filter for Project Model Fields.
 */
export function buildFieldsFilter(projectModelId: string): string {
  return JSON.stringify({
    where: { projectModelId },
    fields: [
      'id', 'name', 'type', 'isRequired', 'isUnique', 'isPrimaryKey',
      'isMixinProperty', 'defaultFn', 'isHidden', 'isArray', 'index',
      'description', 'isId'
    ]
  });
}

/**
 * Pure function to define the payload filter for Project Model Endpoints.
 */
export function buildEndpointsFilter(projectModelId: string): string {
  return JSON.stringify({
    where: { 'modelId': projectModelId },
    fields: [
      'id', 'name', 'type', 'index', 'path', 'description',
      'isCustomEndpoint', 'isRelationEndpoint', 'isMocked',
      'method', 'customRequestTypeName'
    ]
  });
}

/**
 * Pure function to explicitly define the payload filter for Project Model Relations.
 */
export function buildRelationsFilter(projectModelId: string): string {
  return JSON.stringify({
    where: { 'sourceModelId': projectModelId },
    fields: ['id', 'name', 'type', 'description', 'projectModelId', 'sourceModelId', 'targetModelId', 'throughModelId', 'keyFromId', 'keyToId'],
    include: [
      {
        relation: 'sourceModel',
        scope: { fields: ['id', 'title', 'dataTableName', 'description', 'domainName', 'domainKey'] }
      },
      {
        relation: 'targetModel',
        scope: { fields: ['id', 'title', 'dataTableName', 'description', 'domainName', 'domainKey'] }
      },
      {
        relation: 'throughModel',
        scope: { fields: ['id', 'title', 'dataTableName', 'description', 'domainName', 'domainKey'] }
      },
      {
        relation: 'keyFrom',
        scope: { fields: ['id', 'name', 'type', 'description'] }
      },
      {
        relation: 'keyTo',
        scope: { fields: ['id', 'name', 'type', 'description'] }
      }
    ]
  });
}

/**
 * Pure function to safely merge asynchronously fetched fields, endpoints, and relations into a core model object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeProjectModelDetails(baseModel: any, fields: any[], endpoints: any[], relations: any[]): any {
  if (!baseModel) return null;

  const merged = {
    ...baseModel,
    fields: fields || [],
    endpoints: (endpoints || []).sort((a, b) => (a.index || 0) - (b.index || 0)),
    relations: relations || []
  };

  if (!merged.title) {
    merged.title = formatDataTableNameAsTitle(merged.dataTableName || merged.name);
  }

  return merged;
}

/**
 * Pure function to map a local graph data structure for a single project model.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildSingleModelGraphData(model: any): { nodes: any[], edges: any[] } {
  if (!model) return { nodes: [], edges: [] };

  const nodes: any[] = [];
  const edges: any[] = [];

  const mainName = model.name || model.dataTableName || 'Model';

  nodes.push({
    id: mainName,
    shape: 'rect',
    x: 0,
    y: 0,
    width: 220,
    height: 54,
    label: model.title || mainName,
    attrs: {
      body: { fill: 'var(--color-primary)', stroke: 'var(--color-primary)', strokeWidth: 2, rx: 8, ry: 8 },
      label: { fill: 'var(--color-primary-button-text)', fontSize: 15, fontWeight: 'bold' }
    }
  });

  if (model.relations && Array.isArray(model.relations) && model.relations.length > 0) {
    const relatedModelsMap = new Map<string, string>();

    // Pass 1: Deduplicate and resolve target display labels
    model.relations.forEach((rel: any) => {
      const targetObj = rel.targetModel;
      if (targetObj) {
        const targetId = typeof targetObj === 'string' ? targetObj : (targetObj.name || targetObj.id);
        const targetLabel = typeof targetObj === 'string'
          ? formatDataTableNameAsTitle(targetObj)
          : (targetObj.title || formatDataTableNameAsTitle(targetObj.dataTableName || targetObj.name));

        relatedModelsMap.set(targetId, targetLabel);
      }
    });

    const relatedModelIds = Array.from(relatedModelsMap.keys());
    const spacingX = 260;
    const startX = -((relatedModelIds.length - 1) * spacingX) / 2;

    // Pass 2: Draw the target nodes
    relatedModelIds.forEach((relId, index) => {
      nodes.push({
        id: relId,
        shape: 'rect',
        x: startX + index * spacingX,
        y: 160,
        width: 180,
        height: 48,
        label: relatedModelsMap.get(relId),
        attrs: {
          body: { fill: 'var(--color-component-inner)', stroke: 'var(--color-secondary-border)', strokeWidth: 2, rx: 8, ry: 8 },
          label: { fill: 'var(--color-primary-text)', fontSize: 13, fontWeight: '600' }
        }
      });
    });

    // Pass 3: Draw the edges connecting them
    model.relations.forEach((rel: any) => {
      const targetObj = rel.targetModel;
      if (targetObj) {
        const targetId = typeof targetObj === 'string' ? targetObj : (targetObj.name || targetObj.id);
        const targetLabel = relatedModelsMap.get(targetId);

        const throughObj = rel.throughModel;
        const throughLabel = throughObj ? (typeof throughObj === 'string' ? throughObj : (throughObj.title || throughObj.name)) : '';

        const labelText = rel.type === 'hasManyThrough'
          ? `${mainName} has many ${targetLabel}\nthrough ${throughLabel}`
          : `${mainName} ${rel.type} ${targetLabel}`;

        edges.push({
          source: mainName,
          target: targetId,
          router: { name: 'manhattan', args: { padding: 30 } },
          connector: { name: 'rounded', args: { radius: 8 } },
          attrs: {
            line: { stroke: 'var(--color-secondary-text)', strokeWidth: 2, targetMarker: { name: 'classic', size: 8, fill: 'var(--color-secondary-text)' } }
          },
          labels: [{
            attrs: {
              text: { text: labelText, fill: 'var(--color-secondary-text)', fontSize: 11, textAnchor: 'middle', textVerticalAnchor: 'middle' },
              rect: { fill: 'var(--color-body-background)', rx: 8, ry: 8, stroke: 'var(--color-secondary-border)', strokeWidth: 1 }
            }
          }]
        });
      }
    });
  }

  return { nodes, edges };
}